export const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const COOKIE_DOMAIN = '.fozzil.in'; // Shared across all subdomains

/**
 * Shared Storage Utility
 * Uses chunked cookies for cross-subdomain persistence
 * Works for Auth (FOZZIL ID) and Bag (Shopping Cart)
 */
export const sharedStorage = (baseKey) => {
  const getFullCookieName = (key) => key;

  const clearChunks = (key) => {
    if (isLocalhost) {
      localStorage.removeItem(key);
      return;
    }
    try {
      // Clear main key
      document.cookie = `${key}=; domain=${COOKIE_DOMAIN}; path=/; max-age=0; SameSite=Lax; Secure`;
      // Check for and clear potential chunks
      for (let i = 0; i < 10; i++) {
        document.cookie = `${key}_${i}=; domain=${COOKIE_DOMAIN}; path=/; max-age=0; SameSite=Lax; Secure`;
      }
    } catch (e) {
      console.warn(`[sharedStorage] clearChunks failed for ${key}:`, e);
    }
  };

  const getSimple = (key) => {
    if (isLocalhost) return localStorage.getItem(key);
    const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  };

  const setSimple = (key, value, days = 365) => {
    if (isLocalhost) {
      localStorage.setItem(key, value);
      return;
    }
    const maxAge = 60 * 60 * 24 * days;
    document.cookie = `${key}=${encodeURIComponent(value)}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
  };

  return {
    getItem: (key) => {
      const actualKey = key || baseKey;
      if (isLocalhost) return localStorage.getItem(actualKey);
      try {
        const match = document.cookie.match(new RegExp('(^| )' + actualKey + '=([^;]+)'));
        if (!match) return null;
        
        const mainValue = match[2];
        if (!mainValue.startsWith('chunked:')) {
          return decodeURIComponent(mainValue); 
        }
        
        const chunks = parseInt(mainValue.split(':')[1], 10);
        let fullValue = '';
        for (let i = 0; i < chunks; i++) {
          const chunkMatch = document.cookie.match(new RegExp('(^| )' + actualKey + '_' + i + '=([^;]+)'));
          if (chunkMatch) fullValue += chunkMatch[2];
        }
        return decodeURIComponent(fullValue);
      } catch (e) {
        return null;
      }
    },
    setItem: (key, value) => {
      const actualKey = key || baseKey;
      if (isLocalhost) {
        localStorage.setItem(actualKey, value);
        return;
      }
      try {
        const maxAge = 60 * 60 * 24 * 365; // 1 year
        const chunkSize = 3000; // Safe limit
        const encodedValue = encodeURIComponent(value);
        const chunks = Math.ceil(encodedValue.length / chunkSize);
        
        // Clean up old prior chunks
        clearChunks(actualKey);
        
        document.cookie = `${actualKey}=chunked:${chunks}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
        
        for (let i = 0; i < chunks; i++) {
          const chunk = encodedValue.substring(i * chunkSize, (i + 1) * chunkSize);
          document.cookie = `${actualKey}_${i}=${chunk}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
        }
      } catch (e) {
        console.warn(`[sharedStorage] setItem failed for ${actualKey}:`, e);
      }
    },
    removeItem: (key) => {
      const actualKey = key || baseKey;
      clearChunks(actualKey);
    },
    // Simple non-chunked shared keys
    getSimple,
    setSimple: (key, value, days) => {
      if (isLocalhost) {
        localStorage.setItem(key, value);
        return;
      }
      const maxAge = 60 * 60 * 24 * (days || 365);
      document.cookie = `${key}=${encodeURIComponent(value)}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
    }
  };
};
